import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, Building2, ArrowRight, Check } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AppContext";
import { GridBg } from "../components/shared/GridBg";
import { Logo } from "../components/shared/Logo";
import { toast } from "sonner";

const CARDS = [
  { key: "student", icon: GraduationCap, title: "I'm a student / freelancer", desc: "Apply to verified tasks and build a professional reputation with every completion.", pts: ["KYC-verified access", "Nearby task matching", "Earnings & payouts"] },
  { key: "company", icon: Building2,      title: "I represent a company",       desc: "Post one-off tasks and get pre-screened, KYC-verified students allocated by admin.", pts: ["Post & approve tasks", "Vetted talent pool", "Workroom & delivery"] },
];

export default function RoleSelect() {
  const { user, refresh } = useAuth();
  const [choice, setChoice] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const submit = async () => {
    if (!choice) return;
    setSaving(true);
    try {
      await api.post("/auth/role", { role: choice });
      await refresh();
      toast.success("Role set", { description: choice === "student" ? "Let's set up your profile." : "Let's verify your company." });
      navigate(choice === "student" ? "/student/onboarding" : "/company/onboarding", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not set role");
    } finally { setSaving(false); }
  };

  return (
    <GridBg hero className="min-h-screen">
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="flex items-center justify-between mb-16"><Logo/>
          <div className="text-xs text-zinc-500">Signed in as <span className="text-zinc-300">{user?.email}</span></div>
        </div>
        <div className="max-w-2xl">
          <div className="eyebrow text-blue-400">Step 1 of 3</div>
          <h1 className="mt-3 font-display text-4xl md:text-5xl font-medium text-white leading-tight">Tell us who you are on Assisto4U.</h1>
          <p className="mt-4 text-sm text-zinc-500">Pick the role that fits. You can only choose one — admins are provisioned separately.</p>
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-5">
          {CARDS.map(c => {
            const Icon = c.icon;
            const active = choice === c.key;
            return (
              <button key={c.key} data-testid={`role-select-${c.key}`} onClick={() => setChoice(c.key)}
                      className={`text-left p-6 rounded-xl border transition-colors duration-200 relative ${active ? "border-blue-500/50 bg-blue-500/[0.06]" : "border-[#27272A] bg-[#0A0A0C] hover:border-[#3A3A42]"}`}>
                {active && <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center"><Check size={13} className="text-white"/></div>}
                <div className="w-10 h-10 rounded-md flex items-center justify-center mb-4"
                     style={{ background: active ? "rgba(59,130,246,0.15)" : "#111114", border: `1px solid ${active ? "#3B82F6" : "#27272A"}` }}>
                  <Icon size={18} className={active ? "text-blue-400" : "text-zinc-400"}/>
                </div>
                <div className="font-display text-xl text-white">{c.title}</div>
                <p className="text-sm text-zinc-500 mt-2 leading-relaxed">{c.desc}</p>
                <ul className="mt-4 space-y-1.5">
                  {c.pts.map(p => <li key={p} className="text-xs text-zinc-400 flex items-center gap-2"><Check size={11} className="text-emerald-400"/>{p}</li>)}
                </ul>
              </button>
            );
          })}
        </div>

        <div className="mt-8 flex items-center gap-3">
          <button data-testid="role-select-continue-btn" disabled={!choice || saving} onClick={submit}
                  className="btn-primary px-5 py-3 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-50">
            Continue {!saving && <ArrowRight size={14}/>}
          </button>
          <span className="text-xs text-zinc-500">This choice sets up your workspace and cannot be swapped later.</span>
        </div>
      </div>
    </GridBg>
  );
}
